import {
  ds82PdfId,
  ds11PdfId,
  ds64PdfId,
  ds5504PdfId,
  getFormNameByPdfId,
} from "@/utils/Form/formUtils";

/**
 * Product key (display/concept) → API products that are "services" for that product.
 * When the API adds a new service (e.g. a new expedited option), add its name here.
 * To make a service available, set the enabled flag to true.
 * To set a custom title for a service, set the title property.
 */
export const PRODUCT_SERVICES: Record<
  string,
  Array<{ id: string; name: string; enabled: boolean; title?: string }>
> = {
  "DS-82": [
    {
      id: "b1b7c6e3-1d93-4c2f-a46d-5b89af2e3b01",
      name: "DS82-SMART_4W-APVI",
      enabled: true,
      title: "Smart Service",
    },
    {
      id: "08f4a8fc-64f0-4a18-ae10-c4d879bff5a4",
      name: "DS82-EXPEDITED_2W-APVI",
      enabled: true,
      title: "Expedited Service",
    },
    {
      id: "ca54ad2f-1299-4f58-b990-3cd33e7068f7",
      name: "DS82-EXPEDITED_2W-TRAVEL_VISA_PRO",
      enabled: false,
    },
    {
      id: "d3680599-21d6-47ea-919b-fa32d23cf137",
      name: "DS82-EXPEDITED_2D-TRAVEL_VISA_PRO",
      enabled: false,
    },
    {
      id: "584250e6-ceb0-46ae-94a0-cf210f13259d",
      name: "DS82-EXPEDITED_4D-TRAVEL_VISA_PRO",
      enabled: false,
    },
  ],
  "DS-11": [
    {
      id: "7ac3b3aa-10f9-41df-a5fb-bb9ea091f8a2",
      name: "DS11-SMART_4W-TRAVEL_VISA_PRO",
      enabled: true,
      title: "Smart Service",
    },
    {
      id: "2cce26fd-d34c-4356-994b-1739a3e0ffdd",
      name: "DS11-EXPEDITED_2W-TRAVEL_VISA_PRO",
      enabled: true,
      title: "Expedited Service",
    },
    {
      id: "849ca282-c336-4a89-9076-fef1d6433d78",
      name: "DS11-EXPEDITED_4D-TRAVEL_VISA_PRO",
      enabled: false,
    },
    {
      id: "b6d108fd-0e78-4907-a3a0-b1852addedac",
      name: "DS11-EXPEDITED_2D-TRAVEL_VISA_PRO",
      enabled: false,
    },
  ],
  "DS-64": [
    {
      id: "4bbe0991-1a8b-4d0e-adc9-7d42db78acd2",
      name: "DS64-EXPEDITED_2D-TRAVEL_VISA_PRO",
      enabled: false,
    },
    {
      id: "a1b10eab-00f4-4e28-981f-6b71adc52bbd",
      name: "DS64-EXPEDITED_4D-TRAVEL_VISA_PRO",
      enabled: false,
    },
    {
      id: "c86c22f6-6e57-4f8a-9994-33dc5f938520",
      name: "DS64-EXPEDITED_2W-TRAVEL_VISA_PRO",
      enabled: false,
    },
    {
      id: "7817e8d1-1e5e-49be-8dd8-08615a4e1c3d",
      name: "DS64-SMART_4W-TRAVEL_VISA_PRO",
      enabled: false,
    },
  ],
  "DS-5504": [
    {
      id: "8120d30e-9ade-42d8-af85-1aadbd0279bc",
      name: "DS5504-EXPEDITED_2D-TRAVEL_VISA_PRO",
      enabled: false,
    },
    {
      id: "c16f10c7-bbdf-42ea-8367-b9211c721505",
      name: "DS5504-EXPEDITED_4D-TRAVEL_VISA_PRO",
      enabled: false,
    },
    {
      id: "d3646d74-36ab-45fb-82da-40963345f1fb",
      name: "DS5504-EXPEDITED_2W-TRAVEL_VISA_PRO",
      enabled: false,
    },
    {
      id: "b9a8e8a6-0a0f-4f38-a6f0-8a2cfbc7630f",
      name: "DS5504-SMART_4W-TRAVEL_VISA_PRO",
      enabled: false,
    },
  ],
};

const normalizeServiceName = (serviceName?: string): string =>
  (serviceName ?? "").trim().toLowerCase();

export const isSmartService = (serviceName?: string): boolean =>
  normalizeServiceName(serviceName).includes("smart");

export const isExpeditedService = (serviceName?: string): boolean =>
  normalizeServiceName(serviceName).includes("expedited");
export type ServiceSelectedTier = "standard" | "smart" | "expedited";

const BASE_FORM_PDF_IDS = new Set([ds82PdfId, ds11PdfId, ds64PdfId, ds5504PdfId]);

export const getServiceSelectedFromMethod = (selectedMethod: string): ServiceSelectedTier => {
  if (selectedMethod === "form") return "standard";
  if (selectedMethod.includes("SMART")) return "smart";
  if (selectedMethod.includes("EXPEDITED")) return "expedited";
  return "standard";
};

export const getServiceSelectedFromPdfId = (pdfId: string): ServiceSelectedTier => {
  if (BASE_FORM_PDF_IDS.has(pdfId)) return "standard";

  for (const services of Object.values(PRODUCT_SERVICES)) {
    const matchedService = services.find((service) => service.id === pdfId);
    if (matchedService) return getServiceSelectedFromMethod(matchedService.name);
  }

  return "standard";
};

export const getServiceSelectedForAnalytics = ({
  selectedMethod,
  pdfId,
}: {
  selectedMethod?: string | null;
  pdfId?: string | null;
}): ServiceSelectedTier => {
  if (selectedMethod) return getServiceSelectedFromMethod(selectedMethod);
  if (pdfId) return getServiceSelectedFromPdfId(pdfId);
  return "standard";
};

/**
 * Get active service IDs for a given form name
 * @param formName - The form name (e.g., "DS-82")
 * @returns Array of active service IDs
 */
export const getActiveServiceIds = (formName: string): string[] => {
  const services = PRODUCT_SERVICES[formName] || [];
  return services.filter((service) => service.enabled).map((service) => service.id);
};

/**
 * Get the title of a service for a given form name and service name
 * @param formName - The form name (e.g., "DS-82")
 * @param serviceName - The service name (e.g., "DS82-EXPEDITED_2W-APVI")
 * @returns The title of the service
 */
export const getServiceTitle = (formName?: string, serviceName?: string): string => {
  if (!serviceName) return "";
  const service = formName
    ? PRODUCT_SERVICES[formName]?.find((s) => s.name === serviceName)
    : undefined;
  if (service?.title) return service.title;
  if (isSmartService(serviceName)) return "Smart Service";
  if (isExpeditedService(serviceName)) return "Expedited Service";
  return "";
};

/**
 * Get the form name (key) that contains a service with the given id
 * @param serviceId - The service id (e.g., "08f4a8fc-64f0-4a18-ae10-c4d879bff5a4")
 * @returns The form name (e.g., "DS-82") or undefined if not found
 */
export const getFormNameByServiceId = (serviceId: string): string | undefined => {
  for (const [formName, services] of Object.entries(PRODUCT_SERVICES)) {
    const found = services.find((s) => s.id === serviceId);
    if (found) {
      return formName;
    }
  }
  return undefined;
};

/** Base form descriptions used for payment header i18n keys (not service product names). */
const BASE_FORM_DESCRIPTIONS: Record<string, string> = {
  "DS-82": "Passport Renewal",
  "DS-11": "New Passport",
  "DS-64": "Lost or Stolen Passport",
  "DS-5504": "Changes/Corrections or Limited Passport",
};

/**
 * Header title for the base form (DS-11 / DS-82 / …), even when the API product
 * is Smart/Expedited. Resolves from pdfId so it works without FORM_DETAILS
 * (e.g. cross-domain redirect to MPH).
 */
export const getFormHeaderTitle = (
  t: (key: string) => string,
  {
    pdfId,
    apiDescription,
    apiFormName,
    fallback = "",
  }: {
    pdfId?: string | null;
    apiDescription?: string;
    apiFormName?: string;
    fallback?: string;
  }
): string => {
  const baseFormName = pdfId
    ? getFormNameByPdfId(pdfId) || getFormNameByServiceId(pdfId) || null
    : null;

  if (baseFormName && BASE_FORM_DESCRIPTIONS[baseFormName]) {
    const key = `${BASE_FORM_DESCRIPTIONS[baseFormName]} - ${baseFormName} Form`;
    return t(key) || fallback || key;
  }

  if (apiDescription && apiFormName) {
    const key = `${apiDescription} - ${apiFormName} Form`;
    return t(key) || fallback || "";
  }

  return fallback;
};
